import { MdrContactDetailsFormView } from '../view/MdrContactDetailsFormView';
import { templateMdrContactDetailsForm } from '../view/template';
import { BaseWebComponentController } from '../../../../../framework/BaseWebComponentController';
import { MdrContactDetailsFormWebComponent } from '..';
import {
  MdrContactDetailsFormEnabled,
  MdrContactDetailsFormEvents,
  MdrContactDetailsFormValuePrivate,
  MdrContactDetailsFormViewData,
  MdrContactDetailsImportConfig,
} from '../types';
import { DropdownMenuEvents } from '../../../../../ui/gzDropdownMenu/types';

export class MdrContactDetailsFormController extends BaseWebComponentController {
  public component: MdrContactDetailsFormWebComponent;
  public view: MdrContactDetailsFormView;
  public viewConstructor: typeof MdrContactDetailsFormView;

  private isSwitchedToRequired = false;

  constructor(component: MdrContactDetailsFormWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: MdrContactDetailsFormView,
      viewTemplate: templateMdrContactDetailsForm,
    });
  }

  async getDataForView(): Promise<MdrContactDetailsFormViewData> {
    const viewDataValue: Partial<MdrContactDetailsFormValuePrivate> | null = this.component.viewData?.value ?? null;
    const viewDataEnabled: MdrContactDetailsFormEnabled | null = this.component.viewData?.enabled ?? null;
    const hasStackedFormFields = this.component.hasStackedFormFields ?? false;
    const hasSendButtonBelowEmail = this.component.hasSendButtonBelowEmail ?? false;
    const required = this.component.required ?? false;

    return {
      value: {
        fullName: viewDataValue?.fullName ?? '',
        email: viewDataValue?.email ?? '',
        hasVerifiedEmailAddress: viewDataValue?.hasVerifiedEmailAddress ?? false,
        emailVerificationCode: viewDataValue?.emailVerificationCode ?? '',
        phoneNumber: {
          countryCode: viewDataValue?.phoneNumber?.countryCode ?? '',
          subscriberNumber: viewDataValue?.phoneNumber?.subscriberNumber ?? '',
        },
      },
      enabled: {
        fullName: viewDataEnabled?.fullName ?? true,
        email: viewDataEnabled?.email ?? true,
        sendEmailVerificationCode: viewDataEnabled?.sendEmailVerificationCode ?? false,
        emailVerificationCode: viewDataEnabled?.emailVerificationCode ?? false,
        phoneNumber: viewDataEnabled?.phoneNumber ?? true,
        clearData: viewDataEnabled?.clearData ?? false,
      },
      hasStackedFormFields,
      hasSendButtonBelowEmail,
      required,
    };
  }

  public computeEventListeners() {
    return [
      {
        element: this.view.dom.$sendEmailVerificationCodeButton,
        event: 'click',
        callback: this.onSendEmailVerificationCodeButtonClick,
      },
      {
        element: this.view.dom.$clearDataButton,
        event: 'click',
        callback: this.clearData,
      },
      {
        element: this.view.dom.$fullNameFormField,
        event: 'change',
        callback: this.formFieldChange,
      },
      {
        element: this.view.dom.$emailAddressFormField,
        event: 'change',
        callback: this.formFieldChange,
      },
      {
        element: this.view.dom.$emailVerificationCodeFormField,
        event: 'change',
        callback: this.formFieldChange,
      },
      {
        element: this.view.dom.$phoneNumberFormField,
        event: 'change',
        callback: this.formFieldChange,
      },
      {
        // Stop propagating upwards the "close" event emitted by the phone number's countryCode selector
        element: this.view.dom.$phoneNumberFormField,
        event: DropdownMenuEvents.CLOSE,
        callback: (e: Event) => e.stopPropagation(),
      },
    ];
  }

  public importContactDetails(details: MdrContactDetailsImportConfig): void {
    if (!details.hasVerifiedEmailAddress) {
      // Import source is from GZ Account that has never been verified
      this.importUnverifiedContactDetails(details);
      return;
    }

    // Import source is from GZ Account that has been verified before OR source is from Partner's contact details
    this.importVerifiedContactDetails(details);
  }

  public resetFields(): void {
    this.view.dom.$fullNameFormField.resetField();
    this.view.dom.$emailAddressFormField.resetField();
    this.view.dom.$hasVerifiedEmailAddressFormField.resetField();
    this.view.dom.$emailVerificationCodeFormField.resetField();
    this.view.dom.$phoneNumberFormField.resetField();
  }

  private async onSendEmailVerificationCodeButtonClick(): Promise<void> {
    const emailAddress = this.view.dom.$emailAddressFormField.value as string;
    const purpose = this.dependencies.Ext.Constants.EmailVerification.Purpose.REASON_MDR_CONTACT_DETAILS;

    this.view.dom.$sendEmailVerificationCodeButton.disabled = true;

    try {
      await this.dependencies.api.clients.EmailVerification.Generate(emailAddress, purpose).toPromise();
      this.view.cacheVerifiedEmailAddress(emailAddress);
      this.view.updateElementsWhenHasSentEmailVerificationCodeWithSuccess();
      this.view.showSuccessToastEmailVerificationCode();
    } catch (error) {
      this.view.updateElementsWhenHasSentEmailVerificationCodeWithError();
      this.view.showErrorToastEmailVerificationCode(error);
    }
  }

  private clearData(): void {
    this.component.dispatchEvent(
      new CustomEvent(MdrContactDetailsFormEvents.CLEAR_DATA, { bubbles: true, composed: true })
    );

    this.setDefaultFormState();
  }

  private importVerifiedContactDetails(details: MdrContactDetailsImportConfig): void {
    this.importCommonContactDetails(details, true);
  }

  private importUnverifiedContactDetails(details: MdrContactDetailsImportConfig): void {
    this.importCommonContactDetails(details, false);
  }

  private importCommonContactDetails(details: MdrContactDetailsImportConfig, isVerified: boolean): void {
    const hasPhoneNumberFullyDefined =
      !!details.phoneNumber && !!details.phoneNumber.countryCode && !!details.phoneNumber.subscriberNumber;

    details.hasVerifiedEmailAddress = isVerified && !!details.email;
    this.setFormGroupValue(details);
    this.view.dom.$fullNameFormField.readonly = !!details.fullName;
    this.view.dom.$fullNameFormField.validate(true);
    this.view.setEmailVerificationDisclaimerHighlight(!isVerified);
    this.view.dom.$emailAddressFormField.readonly = !!details.email;
    this.view.dom.$emailAddressFormField.validate(true);
    this.view.dom.$sendEmailVerificationCodeButton.disabled = isVerified;
    this.view.dom.$emailVerificationCodeFormField.Input.markAsValid();
    this.view.dom.$emailVerificationCodeFormField.disabled = true;
    this.view.dom.$phoneNumberFormField.readonly = hasPhoneNumberFullyDefined;
    this.view.dom.$phoneNumberFormField.validate(true);
    this.view.dom.$clearDataButton.disabled = false;
  }

  private setDefaultFormState(): void {
    this.resetFormGroupFields();
    this.view.dom.$fullNameFormField.readonly = false;
    this.view.dom.$fullNameFormField.validate(true);
    this.view.setEmailVerificationDisclaimerHighlight(false);
    this.view.dom.$emailAddressFormField.readonly = false;
    this.view.dom.$emailAddressFormField.validate(true);
    this.view.dom.$sendEmailVerificationCodeButton.disabled = true;
    this.view.dom.$phoneNumberFormField.readonly = false;
    this.view.dom.$phoneNumberFormField.validate(true);
    this.view.dom.$clearDataButton.disabled = true;
  }

  private setFormGroupValue(details: MdrContactDetailsImportConfig): void {
    this.view.formGroup.value = {
      fullName: details.fullName,
      email: details.email,
      hasVerifiedEmailAddress: details.hasVerifiedEmailAddress ?? false,
      emailVerificationCode: '',
      phoneNumber: {
        countryCode: details.phoneNumber?.countryCode ?? '',
        subscriberNumber: details.phoneNumber?.subscriberNumber ?? '',
      },
    };
  }

  private resetFormGroupFields(): void {
    this.view.formGroup.value = {
      fullName: '',
      email: '',
      hasVerifiedEmailAddress: false,
      emailVerificationCode: '',
      phoneNumber: {
        countryCode: '',
        subscriberNumber: '',
      },
    };
  }

  private formFieldChange(): void {
    // Set Clear Data button's disabled state
    const hasAtLeastOneFormFieldFull = this.view.hasAtLeastOneFormFieldFull();
    this.view.dom.$clearDataButton.disabled = !hasAtLeastOneFormFieldFull;

    // Check Required validators
    this.checkRequiredValidators(hasAtLeastOneFormFieldFull);
  }

  private checkRequiredValidators(hasAtLeastOneFormFieldFull: boolean): void {
    if (this.component.required) {
      // No need to adjust Required validators. The form is always required.
      return;
    }

    // The form is required dynamically. Must check if form requirement needs to be updated.
    if (hasAtLeastOneFormFieldFull && !this.isSwitchedToRequired) {
      this.addRequiredValidatorsToFormFields();
      this.isSwitchedToRequired = true;
      return;
    }

    if (!hasAtLeastOneFormFieldFull && this.isSwitchedToRequired) {
      this.removeRequiredValidatorsFromFormFields();
      this.isSwitchedToRequired = false;
    }
  }

  private addRequiredValidatorsToFormFields(): void {
    this.view.dom.$fullNameFormField.Validators = [
      this.view.getRequiredValidatorFullName(),
      ...this.view.dom.$fullNameFormField.Validators,
    ];

    this.view.dom.$emailAddressFormField.Validators = [
      this.view.getRequiredValidatorEmail(),
      ...this.view.dom.$emailAddressFormField.Validators,
    ];

    this.view.dom.$emailVerificationCodeFormField.Validators = [
      this.view.getRequiredValidatorEmailVerificationCode(),
      ...this.view.dom.$emailVerificationCodeFormField.Validators,
    ];

    this.view.dom.$phoneNumberFormField.Validators = [this.view.getPhoneNumberValidator(true)];

    this.view.formGroup.validate(true);
  }

  private removeRequiredValidatorsFromFormFields(): void {
    this.view.dom.$fullNameFormField.Validators = this.view.dom.$fullNameFormField.Validators.filter(
      validator => validator !== this.view.getRequiredValidatorFullName()
    );

    this.view.dom.$emailAddressFormField.Validators = this.view.dom.$emailAddressFormField.Validators.filter(
      validator => validator !== this.view.getRequiredValidatorEmail()
    );

    this.view.dom.$emailVerificationCodeFormField.Validators =
      this.view.dom.$emailVerificationCodeFormField.Validators.filter(
        validator => validator !== this.view.getRequiredValidatorEmailVerificationCode()
      );

    this.view.dom.$phoneNumberFormField.Validators = [this.view.getPhoneNumberValidator(false)];

    this.view.formGroup.validate(true);
  }
}
